<template>
  <div v-if="!isClosed" class="notification-banner">
    <div class="notification-banner__content">
      <div class="notification-banner__copy">
        <div class="notification-banner__title">
          <svg
            class="notification-banner__icon"
            width="28"
            height="28"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"
              fill="currentColor"
            />
          </svg>
          IMPORTANT!
          <button class="notification-banner__close" @click="closeBanner">
            <CloseIcon />
          </button>
        </div>
        <div class="notification-banner__text">
          Our platform will be permanently shutting down on February 27, 2026.
          After that date, the app won't be available anymore.
        </div>
        <a
          v-if="!isExpanded"
          href="#"
          class="notification-banner__learn-more"
          @click.prevent="toggleExpand"
        >
          Learn more
        </a>
      </div>
      <div v-if="isExpanded" class="notification-banner__expanded">
        <div class="notification-banner__expanded-text">
          To make sure you can keep using your wallets elsewhere, please take a
          moment to back them up if you haven't already.
        </div>
        <div class="notification-banner__links">
          <a
            href="https://medium.com/citadel-one/backup-file-easy-way-to-restore-your-wallets-605d14fef05f"
            target="_blank"
            rel="noopener noreferrer"
            class="notification-banner__link"
          >
            Here's a guide for you
          </a>
          <a
            href="https://t.me/citadelofficial"
            target="_blank"
            rel="noopener noreferrer"
            class="notification-banner__link"
          >
            For any questions please contact us here
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import CloseIcon from '@/assets/icons/close.svg';

export default {
  name: 'NotificationBanner',
  components: {
    CloseIcon,
  },
  setup() {
    const isClosed = ref(false);
    const isExpanded = ref(false);

    const closeBanner = () => {
      isClosed.value = true;
    };

    const toggleExpand = () => {
      isExpanded.value = !isExpanded.value;
    };

    return {
      isClosed,
      isExpanded,
      closeBanner,
      toggleExpand,
    };
  },
};
</script>

<style lang="scss" scoped>
.notification-banner {
  margin-top: -35px;
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  background: $color-black;
  // border: 1px solid $color-light-gray;
  border-radius: $default-border-radius;
  box-shadow: 0px 4px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;

  @include ipad {
    display: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 6px;
    background: $color-dark-purple;
  }

  &__content {
    position: relative;
    padding: 20px 40px 20px 32px;

    @include ipad {
      padding: 18px 24px 18px 22px;
    }
  }

  &__close {
    position: absolute;
    right: 0;
    cursor: pointer;
    width: 16px;
    height: 16px;
    background: none;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s linear;

    &:hover {
      opacity: 0.7;
    }

    :deep(svg) {
      width: 16px;
      height: 16px;
      
      path {
        fill: $color-light-gray;
        transition: fill 0.2s linear;
      }
    }

    &:hover :deep(svg path) {
      fill: $color-green;
    }
  }

  &__copy {
    width: 100%;
  }

  &__title {
    display: flex;
    align-items: center;
    justify-content: left;
    gap: 10px;
    font-weight: 700;
    font-size: $standard-L1;
    line-height: 24px;
    color: $color-red;
    margin-bottom: 12px;
    text-transform: uppercase;
    position: relative;

    @include ipad {
      font-size: $standard;
      line-height: 22px;
      gap: 8px;
    }
  }

  &__icon {
    width: 28px;
    height: 28px;
    margin-top: -4px;
    flex-shrink: 0;
    color: $color-red;

    @include ipad {
      width: 24px;
      height: 24px;
    }
  }

  &__text {
    font-weight: 400;
    font-size: $standard;
    line-height: 20px;
    color: $color-white;
    margin-bottom: 0;

    @include ipad {
      font-size: $small-L1;
      line-height: 18px;
    }
  }

  &__learn-more {
    flex: 0 0 auto;
    font-weight: 700;
    font-size: $standard;
    line-height: 20px;
    color: $color-white;
    text-decoration: underline;
    transition: color 0.2s linear;
    cursor: pointer;

    @include ipad {
      font-size: $small-L1;
      line-height: 18px;
    }

    @include mobile-l {
      margin-top: 8px;
    }

    &:hover {
      color: $color-green;
    }
  }

  &__expanded {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed $color-light-gray;
    animation: expand 0.3s ease-out;
  }

  &__expanded-text {
    font-weight: 400;
    font-size: $standard;
    line-height: 20px;
    color: $color-white;
    margin-bottom: 16px;

    @include ipad {
      font-size: $small-L1;
      line-height: 18px;
    }
  }

  &__links {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__link {
    font-weight: 700;
    font-size: $small-L2;
    line-height: 20px;
    color: $color-purple;
    text-decoration: underline;
    transition: color 0.2s linear;

    @include ipad {
      font-size: $small;
      line-height: 18px;
    }

    &:hover {
      color: $color-green;
    }
  }
}

@keyframes expand {
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 500px;
    opacity: 1;
  }
}
</style>
